"use client";

import { BlocksIngredient } from "../../../../__generated__/contentful-types";
import { useTextDirectionContext } from "../../../../helpers/TextDirectionContext";
import { slugifyTitle } from "../../../../helpers/contentful/slugifyTitle";
import { notNull } from "../../../../helpers/notNull";
import { useCardStackState } from "../../../../helpers/useCardStackState";
import { CardStack } from "../../../card-stack/card-stack";
import { ComparisonItem } from "../../../card-stack/comparison-item/comparison-item";
import { Kicker } from "../../../kicker/kicker";
import { helpers } from "@contentful/rich-text-types";
import { CosmosIconInfo } from "@cosmos/web-react";
import classNames from "classnames/bind";
import styles from "./ingredient.module.css";

const cx = classNames.bind(styles);

export const Ingredient = ({
  block,
  fiveColumns,
}: {
  block: BlocksIngredient;
  fiveColumns: boolean;
}) => {
  const textDirection = useTextDirectionContext();

  const { cardStackState, cardStackActions } = useCardStackState({
    rtl: textDirection === "rtl",
  });

  return (
    block.ingredient && (
      <div
        className={cx("container")}
        id={
          block.ingredient.title
            ? slugifyTitle(block.ingredient.title)
            : undefined
        }
        /**
         * This handle is used in `IngredientsStage` block to measure how far
         * down the page to render the "sticky" can element.
         */
        data-ingredient-container
      >
        <div className={cx("inner")}>
          <div
            className={cx("content", {
              "content--five-columns": fiveColumns,
            })}
          >
            <div className={cx("article")}>
              <h2 className={cx("title")}>{block.ingredient.title}</h2>
              {block.ingredient.body &&
                block.ingredient.body.json.content.map((item, index) => (
                  <p key={index} className={cx("paragraph")}>
                    {item.content.map((paragraph, index2) => {
                      if (paragraph.nodeType === "hyperlink") {
                        return (
                          <a
                            key={index2}
                            className={cx("link", {
                              "link--white": block.linkColor === "White",
                            })}
                            href={paragraph.data.uri}
                          >
                            {paragraph.content.filter(helpers.isText)[0].value}
                          </a>
                        );
                      }

                      if (helpers.isText(paragraph)) {
                        return paragraph.marks &&
                          paragraph.marks[0] &&
                          paragraph.marks[0].type === "bold" ? (
                          <span
                            key={index2}
                            className={cx(
                              `paragraph--${paragraph.marks[0].type}`,
                            )}
                          >
                            {paragraph.value}
                          </span>
                        ) : (
                          paragraph.value
                        );
                      }
                    })}
                  </p>
                ))}
            </div>
            {block.showFacts === "Yes" ? (
              <div className={cx("facts")}>
                <div className={cx("facts-icon-wrap")}>
                  <CosmosIconInfo className={cx("facts-icon")}></CosmosIconInfo>
                </div>
                <div className={cx("drink-fact-intro")}>
                  {block.factsTitle && (
                    <h3 className={cx("facts-kicker-wrapper")}>
                      <Kicker kind="bare" text={block.factsTitle} />
                    </h3>
                  )}
                  <p className={cx("fact")}>{block.ingredient.fact}</p>
                </div>
                {block.drinkName &&
                  block.ingredient.amount &&
                  block.per250Ml && (
                    <ul role="list" className={cx("drink-fact")}>
                      <li className={cx("drink-name-wrapper")}>
                        <span className={cx("drink-name")}>
                          {block.drinkName}
                        </span>
                      </li>
                      <li className={cx("drink-info")}>
                        <span className={cx("drink-amount")}>
                          {block.ingredient.amount}
                        </span>
                        <span className={cx("per250ml")}>{block.per250Ml}</span>
                      </li>
                    </ul>
                  )}
                <CardStack
                  cards={
                    block.ingredient.comparisonItemsCollection?.items.filter(
                      notNull,
                    ) || []
                  }
                  cardStackState={cardStackState}
                  cardStackActions={cardStackActions}
                  renderCard={({ card }) => <ComparisonItem card={card} />}
                />
                <span className={cx("disclaimer")}>{block.disclaimer}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    )
  );
};
